// Product grid for the POS — large tap targets with search + category filter.
//
// Phase 2 redesign: stock is per-event. When an active event is supplied
// via `eventId`, the grid loads the event's stock allocations and uses
// `qtyIn - qtySold` as the upper bound for each product. Only products that
// are allocated to the event are shown at all — the catalogue is a few
// hundred items and an event carries a few dozen, so listing everything
// buried what the cashier can actually sell. A product allocated but sold
// out stays visible, disabled, as "Habis di event".
//
// Phase 7: Category grouping. We don't have a proper Category entity
// (just a free-form `categoryId` string on each product), so we derive
// categories from the products data at render time. The category bar
// is a horizontal scroll of chips — tap a chip to filter, tap the
// "Semua" chip to clear. When `selectedCategoryId` is null AND we
// pass `groupByCategory`, the grid shows products grouped under
// sticky category headers (no filter applied).

import { useEffect, useMemo, useState } from 'react';
import { Package, ImageIcon, Search, Tag, LayoutGrid, ListFilter, Gift } from 'lucide-react';
import { cn, formatIDR, formatNumber } from '@/lib/format';
import { useProducts } from '@/hooks/useProducts';
import { useEventAllocations } from '@/hooks/useEventAllocations';
import { useCategories } from '@/hooks/useCategories';
import { useCart } from '@/store/cart';
import { SearchInput } from '@/components/ui/SearchInput';
import { EmptyState } from '@/components/ui/EmptyState';
import type { EventPromo, EventStockAllocation, Product } from '@/types/domain';
import { useEventPromos } from '@/hooks/useEventPromos';
import { ProductImage } from '@/components/inventory/ProductImage';

export interface ProductGridProps {
  /** Optional category filter — null = "Semua". */
  selectedCategoryId?: string | null;
  /**
   * If true, show products grouped under their category headers
   * (sticky-positioned). Used when the user wants a "browse by
   * category" view instead of a single-category filter.
   * Default: false (flat grid, controlled by selectedCategoryId).
   */
  groupByCategory?: boolean;
  /** When defined, overrides the default active-only filter. */
  isActive?: boolean;
  /** Show the search bar. Default true. */
  showSearch?: boolean;
  /**
   * Active event id — when provided, stock is read from the event's
   * allocations (`qtyIn - qtySold`) instead of from product. When null,
   * no stock check is applied.
   */
  eventId?: string | null;
  className?: string;
}

const STOCK_LOW_THRESHOLD = 5;
const NO_CATEGORY_KEY = '__none__';

function stockTone(available: number): string {
  if (available <= 0) return 'text-danger';
  if (available < STOCK_LOW_THRESHOLD) return 'text-warning';
  return 'text-ink-subtle';
}

export function ProductGrid({
  selectedCategoryId: controlledCategoryId = null,
  groupByCategory = false,
  isActive = true,
  showSearch = true,
  eventId = null,
  className,
}: ProductGridProps) {
  const { data: products, isLoading } = useProducts({ isActive });
  const { data: allocations } = useEventAllocations(eventId);
  const { data: categoryDocs } = useCategories({ isActive: true });
  const addItem = useCart((s) => s.addItem);
  const [search, setSearch] = useState('');
  // Internal category filter — only used when the grid is uncontrolled
  // (i.e. when the parent doesn't pass `selectedCategoryId`). When
  // controlled, the parent's value wins.
  const [internalCategoryId, setInternalCategoryId] = useState<string | null>(null);
  const isControlled = controlledCategoryId !== undefined && controlledCategoryId !== null;
  const activeCategoryId = isControlled ? controlledCategoryId : internalCategoryId;

  // Reset the internal category when the controlled one changes
  useEffect(() => {
    if (isControlled) setInternalCategoryId(null);
  }, [isControlled, controlledCategoryId]);

  // productId -> every batch of it on this event, with what is left of each.
  // A product can now be carried in several lots, so the grid shows the total
  // and the cashier chooses the lot at add-time.
  const lotsByProduct = useMemo(() => {
    const map = new Map<string, (EventStockAllocation & { onHand: number })[]>();
    for (const a of allocations ?? []) {
      const list = map.get(a.productId) ?? [];
      list.push({ ...a, onHand: Math.max(0, a.qtyIn - a.qtySold) });
      map.set(a.productId, list);
    }
    // Earliest expiry first — the cashier still picks, but the order is the one
    // they almost always want, and an undated lot should not sort above a dated
    // one just because null compares low.
    for (const list of map.values()) {
      list.sort((x, y) => (x.expiryDate ?? Infinity) - (y.expiryDate ?? Infinity));
    }
    return map;
  }, [allocations]);

  const stockByProduct = useMemo(() => {
    const map = new Map<string, number>();
    for (const [pid, lots] of lotsByProduct) {
      map.set(pid, lots.reduce((sum, l) => sum + l.onHand, 0));
    }
    return map;
  }, [lotsByProduct]);

  // productId -> the price this event actually charges. create_transaction()
  // prices every line from the allocation snapshot, NOT the master product —
  // so the grid and cart must display the same source, or an owner repricing
  // a product mid-event would make the till show one number and charge
  // another. Falls back to the master price only when there is no allocation
  // (catalog browsing without an event).
  const priceByProduct = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of allocations ?? []) {
      map.set(a.productId, a.productPrice);
    }
    return map;
  }, [allocations]);

  const effectivePrice = (p: Product): number =>
    eventId ? (priceByProduct.get(p.id) ?? p.price) : p.price;

  // Build a categoryId -> display name lookup. Prefers the Category doc
  // `name`; falls back to the id itself for legacy products that have
  // a free-form string with no matching Category doc.
  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categoryDocs ?? []) {
      map.set(c.id, c.name);
    }
    return map;
  }, [categoryDocs]);

  // Derive the set of category ids actually in use by the current
  // product list. We merge: known Category docs + any orphan free-form
  // ids from products (backward compat). The chip bar shows both.
  // With an event: only what is allocated to it. Without one (catalogue
  // browsing): everything active.
  const visible = useMemo(() => {
    const list = products ?? [];
    return eventId ? list.filter((p) => lotsByProduct.has(p.id)) : list;
  }, [products, eventId, lotsByProduct]);

  const categories = useMemo(() => {
    const ids = new Set<string>();
    // First, all active Category doc ids
    for (const c of categoryDocs ?? []) {
      ids.add(c.id);
    }
    // Then, any product categoryId not already covered (legacy/orphan)
    for (const p of visible) {
      if (p.categoryId) ids.add(p.categoryId);
    }
    // Sort: known categories first (by their sortOrder), then orphans alphabetically
    const knownIds = (categoryDocs ?? []).map((c) => c.id);
    const orphanIds = Array.from(ids).filter((id) => !knownIds.includes(id));
    orphanIds.sort((a, b) => a.localeCompare(b));
    return [
      ...knownIds.sort((a, b) => {
        const ca = (categoryDocs ?? []).find((c) => c.id === a);
        const cb = (categoryDocs ?? []).find((c) => c.id === b);
        return (ca?.sortOrder ?? 0) - (cb?.sortOrder ?? 0) || a.localeCompare(b);
      }),
      ...orphanIds,
    ];
  }, [visible, categoryDocs]);

  // Resolve a categoryId to its display label (id → name, or id itself)
  const labelForCategory = (id: string): string => categoryNameById.get(id) ?? id;

  const filtered = useMemo(() => {
    const list = visible;
    const term = search.trim().toLowerCase();
    return list
      .filter((p) => {
        if (activeCategoryId) {
          const target =
            activeCategoryId === NO_CATEGORY_KEY ? '' : activeCategoryId;
          const pCat = p.categoryId ?? '';
          if (pCat !== target) return false;
        }
        if (term) {
          return (
            p.name.toLowerCase().includes(term) ||
            p.sku.toLowerCase().includes(term)
          );
        }
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visible, search, activeCategoryId]);

  // Group products by category for the "browse" view.
  const grouped = useMemo(() => {
    const map = new Map<string, Product[]>();
    for (const p of filtered) {
      const key = p.categoryId ?? NO_CATEGORY_KEY;
      const list = map.get(key) ?? [];
      list.push(p);
      map.set(key, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => {
      // "Tanpa kategori" always goes last
      if (a === NO_CATEGORY_KEY) return 1;
      if (b === NO_CATEGORY_KEY) return -1;
      return a.localeCompare(b);
    });
  }, [filtered]);

  // Step one only counts. The batches are offered to the cart as options; the
  // split is made on the review step (or for the cashier when there is one).
  const lotOptionsFor = (p: Product) =>
    (lotsByProduct.get(p.id) ?? [])
      .filter((l) => l.onHand > 0)
      .map((l) => ({
        allocationId: l.id,
        lotNumber: l.lotNumber || undefined,
        expiryDate: l.expiryDate,
        unit: l.unit,
        remaining: l.onHand,
      }));

  const addProduct = (p: Product, qty = 1) => {
    if (!eventId) {
      addItem({
        productId: p.id, name: p.name, price: effectivePrice(p), qty,
        lotOptions: [{ allocationId: `catalog:${p.id}`, remaining: Number.MAX_SAFE_INTEGER }],
      });
      return;
    }
    const lots = lotOptionsFor(p);
    if (lots.length === 0) return;
    addItem({ productId: p.id, name: p.name, price: effectivePrice(p), unit: lots[0].unit, lotOptions: lots, qty });
  };

  const handleClick = (p: Product) => {
    const available = eventId ? (stockByProduct.get(p.id) ?? 0) : Infinity;
    if (available <= 0) return;
    addProduct(p);
  };

  // Bundles on this event: one tap puts every component in the cart at the
  // bundle's quantities; the promo itself is applied on the review step.
  const { data: promos } = useEventPromos(eventId);
  const bundles = useMemo(
    () => (promos ?? []).filter((pr) => pr.kind === 'bundle' && pr.isActive && pr.items.length > 0),
    [promos],
  );
  const addBundle = (bundle: EventPromo) => {
    for (const comp of bundle.items) {
      const prod = products?.find((x) => x.id === comp.productId);
      if (!prod) continue;
      addProduct(prod, comp.qty);
    }
  };

  const renderProductCard = (p: Product) => {
    const available = eventId ? (stockByProduct.get(p.id) ?? 0) : Infinity;
    const outOfStock = available <= 0;
    return (
      <button
        key={p.id}
        type="button"
        onClick={() => handleClick(p)}
        disabled={outOfStock}
        aria-label={`Tambah ${p.name} ke keranjang`}
        className={cn(
          'group flex flex-col items-stretch text-left rounded-lg border bg-surface overflow-hidden transition-all',
          'focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1',
          outOfStock
            ? 'border-line opacity-50 cursor-not-allowed'
            : 'border-line hover:border-primary/40 hover:-translate-y-0.5 hover:shadow-hover active:translate-y-0 cursor-pointer',
        )}
      >
        <div className="h-16 sm:h-20 bg-canvas flex items-center justify-center text-neutral shrink-0">
          <ProductImage
            value={p.imageUrl}
            alt={p.name}
            className="w-full h-full object-cover"
            fallback={<ImageIcon size={22} strokeWidth={1.2} />}
          />
        </div>
        <div className="flex flex-col gap-1 p-2.5">
          <p
            className="text-body font-medium text-ink line-clamp-2 leading-snug"
            title={p.name}
          >
            {p.name}
          </p>
          <p className="text-subhead font-display font-semibold text-ink tabular-nums">
            {formatIDR(effectivePrice(p))}
          </p>
          {eventId && (
            <p
              className={cn(
                'text-caption font-medium tabular-nums',
                stockTone(available),
              )}
            >
              {outOfStock
                ? 'Habis di event'
                : `Stok: ${formatNumber(available)}`}
            </p>
          )}
        </div>
      </button>
    );
  };

  const showCategoryBar = categories.length >= 2; // Only show the bar when it's useful

  return (
    <>
    <div className={cn('flex flex-col min-h-0 h-full', className)}>
      {showSearch && (
        <div className="mb-3">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Cari produk berdasarkan nama atau SKU…"
            leftIcon={<Search size={16} />}
          />
        </div>
      )}

      {/* Bundles on this event: one tap adds every component. */}
      {bundles.length > 0 && (
        <div className="mb-2 -mx-1 px-1" data-tour="pos-bundles">
          <div className="flex gap-1.5 overflow-x-auto overscroll-contain pb-1">
            {bundles.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => addBundle(b)}
                title={`Tambah isi bundling: ${b.items.length} produk`}
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-primary/40 bg-primary/10 text-primary px-3 h-8 text-caption font-medium hover:bg-primary/20"
              >
                <Gift size={12} />
                {b.name}
                <span className="tabular-nums text-ink-subtle">{formatIDR(Number(b.config.bundle_price ?? 0))}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category filter chips — horizontal scroll on small screens. */}
      {showCategoryBar && (
        <div
          className="mb-3 -mx-1 px-1"
          role="tablist"
          aria-label="Filter kategori produk"
        >
          <div className="flex gap-1.5 overflow-x-auto overscroll-contain pb-1">
            <CategoryChip
              active={!activeCategoryId}
              onClick={() => setInternalCategoryId(null)}
              label="Semua"
              count={visible.length}
              icon={<LayoutGrid size={12} />}
            />
            {categories.map((c) => {
              const count = visible.filter((p) => p.categoryId === c).length;
              return (
                <CategoryChip
                  key={c}
                  active={activeCategoryId === c}
                  onClick={() => setInternalCategoryId(c)}
                  label={labelForCategory(c)}
                  count={count}
                  icon={<Tag size={12} />}
                />
              );
            })}
            {visible.some((p) => !p.categoryId) && (
              <CategoryChip
                active={activeCategoryId === NO_CATEGORY_KEY}
                onClick={() => setInternalCategoryId(NO_CATEGORY_KEY)}
                label="Tanpa kategori"
                count={visible.filter((p) => !p.categoryId).length}
                icon={<ListFilter size={12} />}
              />
            )}
          </div>
        </div>
      )}

      {eventId && (allocations?.length ?? 0) === 0 && !isLoading && (
        <div className="mb-3 p-3 rounded border border-warning/30 bg-warning/5 text-caption text-warning">
          Event ini belum punya alokasi stok. Set alokasi dari halaman Events → Manage
          Stock, atau transaksi akan gagal di checkout.
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
        {isLoading ? (
          <div className="py-12 text-center text-ink-subtle text-small">
            Loading produk…
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<Package size={24} strokeWidth={1.5} />}
            title={
              search
                ? 'Tidak ada produk yang cocok'
                : activeCategoryId
                  ? 'Tidak ada produk di kategori ini'
                  : 'Belum ada produk'
            }
            description={
              search
                ? 'Coba kata kunci lain atau ubah filter kategori.'
                : 'Tambah produk di menu Inventory dulu.'
            }
          />
        ) : groupByCategory && !activeCategoryId && !search ? (
          // Grouped view: render each category with a sticky header
          <div className="space-y-5">
            {grouped.map(([categoryId, items]) => (
              <section key={categoryId}>
                <header className="sticky top-0 z-10 -mx-1 px-1 py-1.5 bg-canvas/95 backdrop-blur-sm flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-overline uppercase text-ink-subtle font-medium tracking-wider">
                    <Tag size={11} />
                    {categoryId === NO_CATEGORY_KEY
                      ? 'Tanpa kategori'
                      : labelForCategory(categoryId)}
                  </span>
                  <span className="text-caption text-ink-subtle">
                    ({formatNumber(items.length)})
                  </span>
                </header>
                <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {items.map((p) => renderProductCard(p))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          // Flat view (filtered by category or search)
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((p) => renderProductCard(p))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

function CategoryChip({
  active,
  onClick,
  label,
  count,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-small font-medium whitespace-nowrap shrink-0 transition-colors',
        active
          ? 'bg-primary text-white border-primary'
          : 'bg-surface text-ink border-line hover:border-primary/40 hover:text-primary',
      )}
    >
      {icon}
      <span>{label}</span>
      <span
        className={cn(
          'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold leading-none',
          active ? 'bg-white/20 text-white' : 'bg-canvas text-ink-subtle',
        )}
      >
        {count}
      </span>
    </button>
  );
}
